import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue";
const _hoisted_1 = {
  class: "map-controls"
};
const _hoisted_2 = ["disabled"];
const _hoisted_3 = {
  class: "zoom-display"
};
const _hoisted_4 = ["disabled"];
const _hoisted_5 = {
  key: 0,
  class: "coord-info"
};
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { ElRow, ElSwitch } from "element-plus";
import { drawCircleAvatar, drawIcon } from "@/tools/mapImageTool";
import store from "@/store";
import { sliderWidth } from "@/theme/sliderStyle"; // constants
// pre-declaration

export default {
  __name: 'MapCore',
  props: {
    mapData: {
      type: [Object, null],
      required: true
    },
    hexSize: {
      type: Number,
      default: 44
    },
    minScale: {
      type: Number,
      default: 0.5
    },
    maxScale: {
      type: Number,
      default: 1.5
    },
    showCoords: {
      type: Boolean,
      default: true
    },
    showCenterCoords: {
      type: Boolean,
      default: true
    },
    locked: {
      type: Boolean,
      default: false
    },
    operatingCells: {
      type: Array,
      default: () => []
    }
  },
  emits: ['updateCenterCoord', 'clickCell'],

  setup(__props, {
    expose: __expose,
    emit: __emit
  }) {
    // pre-declarations
    const props = __props;
    const emits = __emit; // refs

    const containerRef = ref(null);
    const canvasRef = ref(null);
    const currentScale = ref(1.0);
    const offsetX = ref(0);
    const offsetY = ref(0);
    const centerCoord = ref([0, 0]);
    const showLabel = ref(props.showCoords === false);
    const locked = ref(props.locked);
    const isDragging = ref(false);
    const lastMousePos = ref({
      x: 0,
      y: 0
    });
    const animationFrameId = ref(null);
    const cellData = ref({}); // computed

    const cachedImages = computed(() => store.state.map_images);
    const pieceData = computed(() => {
      if (props.mapData === null) return {};
      return props.mapData.piece_data ?? {};
    }); // const horizontalSpacing = computed(() => props.hexSize * 1.5 * currentScale.value)
    // const verticalSpacing = computed(() => Math.sqrt(3) * props.hexSize * currentScale.value)

    const horizontalSpacing = computed(() => Math.sqrt(3) * props.hexSize * currentScale.value);
    const verticalSpacing = computed(() => 1.5 * props.hexSize * currentScale.value);
    const mapWidth = computed(() => {
      if (!props.mapData) return 17;
      return props.mapData.width;
    });
    const mapHeight = computed(() => {
      if (!props.mapData) return 7;
      return props.mapData.height;
    }); // functions

    const initCanvas = () => {
      const canvas = canvasRef.value;
      const container = containerRef.value;
      if (!canvas || !container) return;
      const {
        width,
        height
      } = container.getBoundingClientRect();
      canvas.width = width;
      canvas.height = height;
      calculateInitialOffset();
      render();
    };

    const calculateInitialOffset = () => {
      const canvas = canvasRef.value;
      if (!canvas) return;
      const [q, r] = centerCoord.value;
      const pixelX = q * horizontalSpacing.value;
      const pixelY = r * verticalSpacing.value + q % 2 * (verticalSpacing.value / 2);
      offsetX.value = -pixelX * currentScale.value;
      offsetY.value = -pixelY * currentScale.value; // console.log('calculateInitialOffset', q, r, offsetX.value, offsetY.value)
    };

    const render = () => {
      const canvas = canvasRef.value;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      cellData.value = {};
      ctx.clearRect(0, 0, canvas.width, canvas.height); // ctx.width = canvas.width * devicePixelRatio
      // ctx.height = canvas.height * devicePixelRatio

      ctx.save();
      if (!props.mapData) return;
      ctx.translate(offsetX.value, offsetY.value);
      const visibleLeft = -offsetX.value - canvas.width * 0.5;
      const visibleTop = -offsetY.value - canvas.height * 0.5;
      const visibleRight = visibleLeft + canvas.width;
      const visibleBottom = visibleTop + canvas.height;
      const startQ = Math.floor(visibleLeft / horizontalSpacing.value);
      const endQ = Math.ceil(visibleRight / horizontalSpacing.value);
      const startR = Math.floor(visibleTop / verticalSpacing.value);
      const endR = Math.ceil(visibleBottom / verticalSpacing.value); // const limitR = Math.floor(mapHeight.value * 0.5)

      const limitQ = Math.floor(mapWidth.value * 0.5);
      const cellSize = props.hexSize * currentScale.value;
      const mapDataKeys = props.mapData ? Object.keys(props.mapData.data) : []; // console.log(JSON.stringify(props.mapData?.data ?? null))

      for (let q = startQ; q <= endQ; q++) {
        for (let r = startR; r <= endR; r++) {
          if (q < -mapWidth.value * 0.5 || q > mapWidth.value * 0.5 || r < -mapHeight.value * 0.5 || r > mapHeight.value * 0.5) continue; // if (q > 0 && q % 2 === 1 && r === limitR) continue
          // if (q < 0 && q % 2 === -1 && r === -limitR) continue

          if (r > 0 && r % 2 === 1 && q === limitQ) continue;
          if (r < 0 && r % 2 === -1 && q === -limitQ) continue; // const x = q * horizontalSpacing.value + canvas.width * 0.5
          // const y = r * verticalSpacing.value + (q % 2) * (verticalSpacing.value / 2) + canvas.height * 0.5

          const x = q * horizontalSpacing.value + canvas.width * 0.5 + r % 2 * (horizontalSpacing.value / 2);
          const y = r * verticalSpacing.value + canvas.height * 0.5;
          const cellKey = [q, r].join(',');
          cellData.value[cellKey] = [x, y];

          if (mapDataKeys.includes(cellKey)) {
            const extraStyle = props.mapData.data[cellKey];
            drawHexagon(ctx, x, y, cellSize, `${q},${r}`, extraStyle);
            continue;
          }

          drawHexagon(ctx, x, y, cellSize, `${q},${r}`);
        }
      }

      const recordKeys = Object.keys(cellData.value);
      const operating = props.operatingCells && props.operatingCells.length > 0;

      if (operating) {
        for (const cellKey of props.operatingCells) {
          if (recordKeys.includes(cellKey)) {
            const [x, y] = cellData.value[cellKey];
            drawHexagon(ctx, x, y, cellSize, '', {
              'fill_color': 'transparent',
              'stroke_color': '#006A8E'
            });
          }
        }
      }

      for (const [cellKey, pieceInfo] of Object.entries(pieceData.value)) {
        if (recordKeys.includes(cellKey)) {
          const [x, y] = cellData.value[cellKey];
          drawSpritePiece(ctx, x, y, cellSize * 1.2, pieceInfo);
        }
      }

      ctx.restore(); // if (!locked.value) {
      //   drawCenterMarker(ctx)
      // }
    };

    const drawHexagon = (ctx, x, y, size, label, extraStyle = null) => {
      ctx.beginPath(); // if (extraStyle) {
      //   console.log(JSON.stringify(extraStyle))
      // }

      for (let i = 0; i < 6; i++) {
        const angle = Math.PI / 3 * i + Math.PI / 6; // const angle = (Math.PI / 3) * i

        const vertexX = x + size * Math.cos(angle);
        const vertexY = y + size * Math.sin(angle);

        if (i === 0) {
          ctx.moveTo(vertexX, vertexY);
        } else {
          ctx.lineTo(vertexX, vertexY);
        }
      }

      ctx.closePath(); // let fillColor = locked.value ? '#F0F0F0' : '#FFFFFF'

      let fillColor = '#FFFFFF';

      if (extraStyle && extraStyle.fill_color) {
        fillColor = extraStyle.fill_color;
      }

      ctx.fillStyle = fillColor;
      ctx.fill();
      let strokeColor = locked.value ? '#EEEEEE' : '#DEDEDE';

      if (extraStyle && extraStyle.stroke_color) {
        strokeColor = extraStyle.stroke_color;
      }

      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 1;
      ctx.stroke();
      let fontSize = 12;

      if (extraStyle && extraStyle.font_size) {
        fontSize = extraStyle.font_size;
      }

      fontSize = fontSize * currentScale.value;
      let fontColor = '#333';

      if (extraStyle && extraStyle.font_color) {
        fontColor = extraStyle.font_color;
      }

      let fontWeight = 'normal';

      if (extraStyle && extraStyle.font_weight) {
        fontWeight = 'bold';
      }

      if (extraStyle && extraStyle.image_data) {
        drawSpriteIcon(ctx, x, y, size * Math.sqrt(3), extraStyle.image_data);
      }

      if (extraStyle && extraStyle.label) {
        drawCellText(ctx, x, y, size, extraStyle.label, fontColor, fontSize, fontWeight);
      } else if (!showLabel.value) {
        drawCellText(ctx, x, y, size, label, fontColor, fontSize, fontWeight);
      }
    };

    const drawCellText = (ctx, x, y, size, text, color, fontSize, fontWeight) => {
      if (!text || text.length === 0) return;
      ctx.fillStyle = color;
      ctx.font = `${fontWeight} ${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const maxWidth = size * 1.4;
      const lineHeight = fontSize * 1.2;
      const textWidth = ctx.measureText(text).width;
      const lineTextLength = Math.floor(maxWidth / fontSize);

      if (textWidth <= maxWidth) {
        ctx.fillText(text, x, y);
        return;
      }

      let line1 = text.substring(0, lineTextLength).trim();
      let line2 = text.substring(lineTextLength, lineTextLength * 2).trim();
      ctx.fillText(line1, x, y - lineHeight / 2);
      ctx.fillText(line2, x, y + lineHeight / 2);
    };

    const drawSpriteIcon = (ctx, x, y, size, imageData) => {
      if (!Object.keys(cachedImages.value).includes(imageData.imagePath)) return;
      const longSide = Math.max(imageData.width, imageData.height);
      let scale = size * 1.0 / longSide;

      if (imageData.scale) {
        scale *= imageData.scale;
      }

      const offsetX = x - imageData.width * scale * 0.5;
      const offsetY = y - imageData.height * scale * 0.5;
      drawIcon(ctx, cachedImages.value[imageData.imagePath], imageData.x, imageData.y, imageData.width, imageData.height, offsetX, offsetY, scale);
    };

    const drawSpritePiece = (ctx, x, y, size, imageData) => {
      if (!Object.keys(cachedImages.value).includes(imageData.imagePath)) return;
      const longSide = Math.max(imageData.width, imageData.height);
      const scale = size / longSide;
      const offsetX = x - imageData.width * scale * 0.5;
      const offsetY = y - imageData.height * scale * 0.5;
      drawCircleAvatar(ctx, cachedImages.value[imageData.imagePath], imageData.x, imageData.y, imageData.width, imageData.height, offsetX, offsetY, scale, '#444444', 1);
    }; // const drawCenterMarker = (ctx) => {
    //   const canvas = canvasRef.value
    //   if (!canvas) return
    //
    //   const centerX = canvas.width / 2
    //   const centerY = canvas.height / 2
    //
    //   ctx.save()
    //   ctx.translate(centerX, centerY)
    //
    //   ctx.strokeStyle = '#0D6E6E'
    //   ctx.lineWidth = 1
    //
    //   ctx.beginPath()
    //   ctx.moveTo(-7, 0)
    //   ctx.lineTo(7, 0)
    //   ctx.moveTo(0, -7)
    //   ctx.lineTo(0, 7)
    //   ctx.stroke()
    //
    //   ctx.restore()
    // }


    const requestRender = () => {
      if (animationFrameId.value) {
        cancelAnimationFrame(animationFrameId.value);
      }

      animationFrameId.value = requestAnimationFrame(() => {
        render();
        animationFrameId.value = null;
      });
    };

    const handleResize = () => {
      setTimeout(() => {
        initCanvas();
      }, 200);
    };

    const setCenterCoord = coord => {
      if (coord && coord.length === 2) {
        centerCoord.value = coord;
        calculateInitialOffset();
        render();
      }
    };

    const handleZoomIn = () => {
      const canvas = canvasRef.value;

      if (canvas) {
        handleZoom(0.1);
      }
    };

    const handleZoomOut = () => {
      const canvas = canvasRef.value;

      if (canvas) {
        handleZoom(-0.1);
      }
    };

    const handleWheel = event => {
      if (locked.value) return;
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.1 : 0.1;
      handleZoom(delta);
    };

    const handleZoom = delta => {
      const canvas = canvasRef.value;
      if (!canvas) return;
      const newScale = Math.max(props.minScale, Math.min(props.maxScale, currentScale.value + delta));
      if (newScale === currentScale.value) return;
      offsetX.value = offsetX.value / currentScale.value * newScale;
      offsetY.value = offsetY.value / currentScale.value * newScale;
      currentScale.value = newScale;
      requestRender();
    };

    const handleMouseDown = event => {
      if (locked.value) return;
      isDragging.value = true;
      lastMousePos.value = {
        x: event.clientX,
        y: event.clientY
      };
    };

    const handleMouseMove = event => {
      if (!isDragging.value) return;
      const canvas = canvasRef.value;
      if (!canvas) return;
      const deltaX = event.clientX - lastMousePos.value.x;
      const deltaY = event.clientY - lastMousePos.value.y;
      lastMousePos.value = {
        x: event.clientX,
        y: event.clientY
      };
      const limitX = canvas.width * 0.5 * currentScale.value;
      const limitY = canvas.height * 0.5 * currentScale.value;
      const newOffsetX = Math.max(-limitX, Math.min(limitX, offsetX.value + deltaX));
      const newOffsetY = Math.max(-limitY, Math.min(limitY, offsetY.value + deltaY));
      offsetX.value = newOffsetX;
      offsetY.value = newOffsetY;
      requestRender();
      updateCenterCoord();
    };

    const handleMouseUp = () => {
      isDragging.value = false;
    };

    const updateCenterCoord = () => {
      const canvas = canvasRef.value;
      if (!canvas) return;
      const [q, r] = pixelToHex(-offsetX.value, -offsetY.value);
      centerCoord.value = [q, r];
      emits('updateCenterCoord', [q, r]);
    };

    const pixelToHex = (x, y) => {
      // 取近似，要进一步精确边界考虑在绘制时存储每一个六边形中心坐标点
      // todo 每个点位最多被三个周边六边形争取，通过近似点位取三个欧几里得距离，获得精确结果
      // const xSide = props.hexSize * currentScale.value * 1.5
      // const ySide = props.hexSize * currentScale.value * Math.sqrt(3)
      //
      // const q = Math.floor(((x + xSide * 0.5) / xSide))
      //
      // let r
      // if (q % 2 === 0) {
      //   r = Math.floor((y + ySide * 0.5) / ySide)
      // } else {
      //   r = Math.floor(q > 0 ? ((y) / ySide) : ((y + ySide) / ySide))
      // }
      const xSide = props.hexSize * currentScale.value * Math.sqrt(3);
      const ySide = props.hexSize * currentScale.value * 1.5;
      const r = Math.floor((y + ySide * 0.5) / ySide);
      let q;

      if (r % 2 === 0) {
        q = Math.floor((x + xSide * 0.5) / xSide);
      } else {
        q = Math.floor(r > 0 ? x / xSide : (x + xSide) / xSide);
      }

      return [q, r];
    };

    const handleCanvasClick = event => {
      const canvas = canvasRef.value;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const touchX = event.clientX - rect.left - canvas.width * 0.5 - offsetX.value;
      const touchY = event.clientY - rect.top - canvas.height * 0.5 - offsetY.value;
      const [q, r] = pixelToHex(touchX, touchY); // console.log(`canvasClick (${touchX}, ${touchY}) => (${q}, ${r})`)

      emits('clickCell', [q, r]);
    }; // lifecycle


    onMounted(() => {
      nextTick(() => {
        initCanvas();
        window.addEventListener('resize', handleResize);
        const canvas = canvasRef.value;
        canvas.addEventListener('click', handleCanvasClick);
      });
    });
    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);

      if (animationFrameId.value) {
        cancelAnimationFrame(animationFrameId.value);
      }

      const canvas = canvasRef.value;

      if (canvas) {
        canvas.removeEventListener('click', handleCanvasClick);
      }
    }); // watcher

    watch(() => sliderWidth, () => {
      setTimeout(() => {
        handleResize();
      }, 200);
    }, {
      deep: true
    }); // post-declarations

    __expose({
      requestRender,
      setCenterCoord
    });

    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock("div", {
        class: "container",
        ref_key: "containerRef",
        ref: containerRef
      }, [_createElementVNode("canvas", {
        ref_key: "canvasRef",
        ref: canvasRef,
        onWheel: handleWheel,
        onMousedown: handleMouseDown,
        onMousemove: handleMouseMove,
        onMouseup: handleMouseUp,
        onMouseleave: handleMouseUp
      }, null, 544), _createElementVNode("div", _hoisted_1, [_createVNode(_unref(ElSwitch), {
        modelValue: showLabel.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => showLabel.value = $event),
        "active-text": showLabel.value ? '显示标签' : '',
        "inactive-text": showLabel.value ? '' : '显示坐标',
        "inline-prompt": "",
        onChange: requestRender
      }, null, 8, ["modelValue", "active-text", "inactive-text"]), _createVNode(_unref(ElSwitch), {
        modelValue: locked.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => locked.value = $event),
        "active-text": locked.value ? '地图锁定' : '',
        "inactive-text": locked.value ? '' : '自由移动',
        "inline-prompt": "",
        onChange: requestRender
      }, null, 8, ["modelValue", "active-text", "inactive-text"]), _createVNode(_unref(ElRow), {
        class: "zoom-controls"
      }, {
        default: _withCtx(() => [_createElementVNode("button", {
          class: "zoom-btn",
          onClick: handleZoomOut,
          disabled: currentScale.value <= __props.minScale
        }, " - ", 8, _hoisted_2), _createElementVNode("span", _hoisted_3, _toDisplayString(Math.round(currentScale.value * 100)) + "%", 1), _createElementVNode("button", {
          class: "zoom-btn",
          onClick: handleZoomIn,
          disabled: currentScale.value >= __props.maxScale
        }, " + ", 8, _hoisted_4)]),
        _: 1
      })]), __props.showCenterCoords ? (_openBlock(), _createElementBlock("div", _hoisted_5, " 中心坐标: [" + _toDisplayString(centerCoord.value[0]) + ", " + _toDisplayString(centerCoord.value[1]) + "] ", 1)) : _createCommentVNode("", true)], 512);
    };
  }

};